<script setup>
import {
  computed,
  onMounted,
  onBeforeUnmount,
  useTemplateRef,
  ref,
  getCurrentInstance,
} from 'vue';
import { usePreferredReducedMotion } from '@vueuse/core';
import Icon from 'next/icon/Icon.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { downloadFile } from '@chatwoot/utils';
import DebtAudioTranscriptionApi from 'dashboard/api/debtAudioTranscription';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';

const { attachment } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  showTranscribedText: {
    type: Boolean,
    default: true,
  },
});

defineOptions({
  inheritAttrs: false,
});

const timeStampURL = computed(() => {
  return timeStampAppendedURL(attachment.dataUrl);
});

const TRANSCRIPT_PREVIEW_LENGTH = 200;
const isTranscriptExpanded = ref(false);

// Contrato novo: attachment.transcription = { status, text, model } chega em
// todo anexo de áudio real processado pelo pipeline de transcrição
// (enterprise/app/services/messages/audio_transcription_service.rb), com
// status null para "nunca tentado". Ausência do campo inteiro só acontece em
// anexos sintéticos que não passam por esse pipeline — a gravação de chamada
// de voz montada à mão em VoiceCall.vue — e cai no texto plano antigo
// (attachment.transcribedText), sem os rótulos de estado novos.
// DEBT customization — transcrição SOB DEMANDA.
//
// O estado local tem precedência sobre a prop: o resultado chega por consulta
// (o job roda em segundo plano) e a prop só se atualizaria num novo carregamento
// da conversa. Sem isto o agente clicaria, esperaria, e não veria nada mudar.
const localTranscription = ref(null);
const transcription = computed(
  () => localTranscription.value ?? attachment.transcription ?? null
);

// `idle` | `running` | `error`. `running` cobre o POST E a espera pelo
// resultado — para quem olha a tela é um estado só.
const requestState = ref('idle');
let pollTimer = null;

const TERMINAL = ['transcribed', 'empty', 'skipped', 'failed'];
const POLL_INTERVAL_MS = 3000;
// 40 tentativas x 3s = 2 min. O teto existe porque o job pode nunca rodar (fila
// cheia, teto diário estourado): sem ele o botão ficaria "Transcrevendo…" para
// sempre, que é pior que voltar e deixar pedir de novo.
const POLL_MAX_TRIES = 40;

const stopPolling = () => {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = null;
};

const poll = async triesLeft => {
  if (triesLeft <= 0) {
    requestState.value = 'idle';
    return;
  }
  try {
    const { data } = await DebtAudioTranscriptionApi.show(attachment.id);
    localTranscription.value = data;
    if (TERMINAL.includes(data?.status)) {
      requestState.value = 'idle';
      return;
    }
  } catch {
    // Falha de consulta não é falha da transcrição: segue tentando até o teto.
  }
  pollTimer = setTimeout(() => poll(triesLeft - 1), POLL_INTERVAL_MS);
};

const requestTranscription = async () => {
  if (requestState.value === 'running') return;
  requestState.value = 'running';
  try {
    const { data } = await DebtAudioTranscriptionApi.request(attachment.id);
    localTranscription.value = data;
    if (TERMINAL.includes(data?.status)) {
      requestState.value = 'idle';
      return;
    }
    poll(POLL_MAX_TRIES);
  } catch {
    requestState.value = 'error';
  }
};

onBeforeUnmount(stopPolling);
const transcriptionStatus = computed(() => transcription.value?.status ?? null);

// A ordem aqui e o defeito que quase foi para producao. `transcription` agora
// existe em TODO anexo de audio (o payload devolve `{status: null}` quando nao
// ha linha), entao testar o OBJETO fazia o ramo legado virar codigo morto: um
// audio transcrito pela stack de API ANTES desta tabela existir tem o texto so
// em `meta['transcribed_text']` e nenhuma linha -- e apareceria como
// "aguardando transcricao", escondendo um texto que o agente le hoje. Pior: a
// busca continuaria mostrando esse mesmo texto, porque ela le o campo antigo.
// Quem decide e o STATUS, nao a presenca do objeto.
const transcribedText = computed(() => {
  const status = transcriptionStatus.value;
  if (status === 'transcribed') return transcription.value.text || '';
  // Estado terminal conhecido (empty/skipped/failed): nao ha texto, e o rotulo
  // de estado e que explica por que.
  if (status) return '';
  // `status === null` significa "nunca tentado por ESTA stack" -- e ai o texto
  // legado, se existir, continua sendo a verdade.
  return attachment.transcribedText || '';
});

const isTranscriptLong = computed(
  () => transcribedText.value.length > TRANSCRIPT_PREVIEW_LENGTH
);
const displayedTranscript = computed(() => {
  const text = transcribedText.value;
  if (!isTranscriptLong.value || isTranscriptExpanded.value) return text;
  return `${text.slice(0, TRANSCRIPT_PREVIEW_LENGTH).trimEnd()}…`;
});
const showsTranscribedText = computed(() => Boolean(transcribedText.value));

// empty/skipped/failed não são erro de rede nem carregamento — o rótulo
// textual é a única forma de diferenciar "nunca tentado" de "tentou e não
// achou fala" de "não vai tentar de novo"; antes desta mudança os três (e o
// silêncio de um áudio que simplesmente não tem transcrição ainda) eram a
// mesma tela em branco. Cores e ícones espelham o padrão já validado das
// pastilhas do Kanban (KanbanConditionPill.vue / kanbanConditions.js:8-14):
// bg-n-{cor}-3 + text-n-{cor}-12, nunca -11 (reprovou WCAG 1.4.3 no claro).
const AUDIO_TRANSCRIPTION_STATUS_ROW = {
  empty: {
    icon: 'i-lucide-mic-off',
    labelKey: 'CONVERSATION.AUDIO_TRANSCRIPTION.EMPTY',
    classes: 'bg-n-slate-3 text-n-slate-12',
  },
  skipped: {
    icon: 'i-lucide-circle-slash',
    labelKey: 'CONVERSATION.AUDIO_TRANSCRIPTION.SKIPPED',
    classes: 'bg-n-slate-3 text-n-slate-12',
  },
  failed: {
    icon: 'i-lucide-triangle-alert',
    labelKey: 'CONVERSATION.AUDIO_TRANSCRIPTION.FAILED',
    classes: 'bg-n-ruby-3 text-n-ruby-12',
  },
};
const PENDING_STATUS_ROW = {
  icon: 'i-lucide-clock',
  labelKey: 'CONVERSATION.AUDIO_TRANSCRIPTION.PENDING',
  classes: 'bg-n-slate-3 text-n-slate-12',
};

// Botão só quando há sidecar configurado (`can_request`) e nada foi tentado
// ainda. Com a stack automática ligada o rótulo "aguardando" continua sendo
// verdade e o botão não precisa existir — mas oferecê-lo não atrapalha, e o
// backend é idempotente: com registro já gravado ele não enfileira de novo.
const canRequestTranscription = computed(
  () =>
    transcription.value?.can_request === true &&
    transcriptionStatus.value === null
);

const statusRow = computed(() => {
  if (canRequestTranscription.value) return null;
  if (!transcription.value || showsTranscribedText.value) return null;
  return (
    AUDIO_TRANSCRIPTION_STATUS_ROW[transcriptionStatus.value] ||
    PENDING_STATUS_ROW
  );
});

// A transição de pendente para transcrito pode acontecer com a mensagem já
// aberta na tela (job de transcrição termina em segundo plano); fade-in-up é
// o keyframe já existente do projeto (tailwind.config.js:218-261) — nenhuma
// biblioteca de animação nova. Sem movimento nenhum sob
// prefers-reduced-motion, mesmo padrão de KanbanColumn.vue:24-25.
const prefersReducedMotion = usePreferredReducedMotion();
const revealTransitionClass = computed(() =>
  prefersReducedMotion.value === 'reduce' ? '' : 'animate-fade-in-up'
);

const audioPlayer = useTemplateRef('audioPlayer');

const isPlaying = ref(false);
const isMuted = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playbackSpeed = ref(1);

const { uid } = getCurrentInstance();

// MediaRecorder-produced WebM/Opus blobs lack a Duration header → <audio>.duration
// resolves to Infinity until we seek past the end, which forces the engine to
// scan the file and compute the real length. Safe no-op for files with a real
// duration already (mp3/m4a/etc).
const resolveStreamingDuration = () => {
  const el = audioPlayer.value;
  if (!el) return;
  const onTimeUpdate = () => {
    el.removeEventListener('timeupdate', onTimeUpdate);
    el.currentTime = 0;
    duration.value = el.duration;
  };
  el.addEventListener('timeupdate', onTimeUpdate);
  try {
    el.currentTime = Number.MAX_SAFE_INTEGER;
  } catch {
    el.removeEventListener('timeupdate', onTimeUpdate);
  }
};

const onLoadedMetadata = () => {
  const d = audioPlayer.value?.duration;
  if (!Number.isFinite(d)) {
    resolveStreamingDuration();
    return;
  }
  duration.value = d;
};

const playbackSpeedLabel = computed(() => {
  return `${playbackSpeed.value}x`;
});

// There maybe a chance that the audioPlayer ref is not available
// When the onLoadMetadata is called, so we need to set the duration
// value when the component is mounted
onMounted(() => {
  const d = audioPlayer.value?.duration;
  if (Number.isFinite(d)) duration.value = d;
  audioPlayer.value.playbackRate = playbackSpeed.value;
});

// Listen for global audio play events and pause if it's not this audio
useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && isPlaying.value) {
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore pause errors */
    }
    isPlaying.value = false;
  }
});

const formatTime = time => {
  if (!time || Number.isNaN(time)) return '00:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const toggleMute = () => {
  audioPlayer.value.muted = !audioPlayer.value.muted;
  isMuted.value = audioPlayer.value.muted;
};

const onTimeUpdate = () => {
  currentTime.value = audioPlayer.value?.currentTime;
};

const seek = event => {
  const time = Number(event.target.value);
  audioPlayer.value.currentTime = time;
  currentTime.value = time;
};

const playOrPause = () => {
  if (isPlaying.value) {
    audioPlayer.value.pause();
    isPlaying.value = false;
  } else {
    // Emit event to pause all other audio
    emitter.emit('pause_playing_audio', uid);
    audioPlayer.value.play();
    isPlaying.value = true;
  }
};

const onEnd = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  playbackSpeed.value = 1;
  audioPlayer.value.playbackRate = 1;
};

const changePlaybackSpeed = () => {
  const speeds = [1, 1.5, 2];
  const currentIndex = speeds.indexOf(playbackSpeed.value);
  const nextIndex = (currentIndex + 1) % speeds.length;
  playbackSpeed.value = speeds[nextIndex];
  audioPlayer.value.playbackRate = playbackSpeed.value;
};

const downloadAudio = async () => {
  const { fileType, dataUrl, extension } = attachment;
  downloadFile({ url: dataUrl, type: fileType, extension });
};
</script>

<template>
  <audio
    ref="audioPlayer"
    controls
    class="hidden"
    playsinline
    @loadedmetadata="onLoadedMetadata"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
  >
    <source :src="timeStampURL" />
  </audio>
  <div
    v-bind="$attrs"
    class="rounded-xl w-full gap-2 p-1.5 bg-n-alpha-white flex flex-col items-center border border-n-container shadow-[0px_2px_8px_0px_rgba(94,94,94,0.06)]"
  >
    <div class="flex gap-1 w-full flex-1 items-center justify-start">
      <button class="p-0 border-0 size-8" @click="playOrPause">
        <Icon
          v-if="isPlaying"
          class="size-8"
          icon="i-teenyicons-pause-small-solid"
        />
        <Icon v-else class="size-8" icon="i-teenyicons-play-small-solid" />
      </button>
      <div class="tabular-nums text-xs">
        {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
      </div>
      <div class="flex-1 items-center flex px-2">
        <input
          type="range"
          min="0"
          :max="duration"
          :value="currentTime"
          class="w-full h-1 bg-n-slate-12/40 rounded-lg appearance-none cursor-pointer accent-current"
          @input="seek"
        />
      </div>
      <button
        class="border-0 w-10 h-6 grid place-content-center bg-n-alpha-2 hover:bg-alpha-3 rounded-2xl"
        @click="changePlaybackSpeed"
      >
        <span class="text-xs text-n-slate-11 font-medium">
          {{ playbackSpeedLabel }}
        </span>
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="toggleMute"
      >
        <Icon v-if="isMuted" class="size-4" icon="i-lucide-volume-off" />
        <Icon v-else class="size-4" icon="i-lucide-volume-2" />
      </button>
      <button
        class="p-0 border-0 size-8 grid place-content-center"
        @click="downloadAudio"
      >
        <Icon class="size-4" icon="i-lucide-download" />
      </button>
    </div>

    <div
      v-if="showTranscribedText && showsTranscribedText"
      data-testid="audio-transcript-text"
      class="text-n-slate-12 p-3 text-sm bg-n-alpha-1 rounded-lg w-full break-words"
      :class="revealTransitionClass"
    >
      {{ displayedTranscript }}
      <button
        v-if="isTranscriptLong"
        class="block mt-1 p-0 border-0 bg-transparent text-n-slate-11 hover:text-n-slate-12 font-medium"
        @click="isTranscriptExpanded = !isTranscriptExpanded"
      >
        {{
          isTranscriptExpanded
            ? $t('CONVERSATION.VOICE_CALL.TRANSCRIPT_SHOW_LESS')
            : $t('CONVERSATION.VOICE_CALL.TRANSCRIPT_SHOW_MORE')
        }}
      </button>
    </div>
    <!-- BOTÃO em vez de promessa: "aguardando transcrição" só é verdade quando
         existe algo que vá transcrever sozinho. Com o sidecar configurado e a
         varredura automática desligada, o honesto é OFERECER a ação. -->
    <button
      v-else-if="showTranscribedText && canRequestTranscription"
      type="button"
      data-testid="audio-transcription-request"
      :disabled="requestState === 'running'"
      :aria-label="$t('CONVERSATION.AUDIO_TRANSCRIPTION.REQUEST_ARIA')"
      class="self-start inline-flex items-center gap-1 h-5 px-1.5 rounded text-xs font-medium shrink-0 bg-n-slate-3 text-n-slate-12 hover:bg-n-slate-4 disabled:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
      @click="requestTranscription"
    >
      <span
        :class="
          requestState === 'running'
            ? 'i-lucide-loader-circle animate-spin'
            : 'i-lucide-captions'
        "
        class="size-3 shrink-0"
        aria-hidden="true"
      />
      <span>{{
        requestState === 'running'
          ? $t('CONVERSATION.AUDIO_TRANSCRIPTION.REQUESTING')
          : requestState === 'error'
            ? $t('CONVERSATION.AUDIO_TRANSCRIPTION.REQUEST_FAILED')
            : $t('CONVERSATION.AUDIO_TRANSCRIPTION.REQUEST')
      }}</span>
    </button>
    <div
      v-else-if="showTranscribedText && statusRow"
      role="status"
      data-testid="audio-transcription-status"
      class="self-start inline-flex items-center gap-1 h-5 px-1.5 rounded text-xs font-medium shrink-0"
      :class="[statusRow.classes, revealTransitionClass]"
    >
      <!--
        span direto, não <Icon>: Icon.vue resolve o ícone via
        `<component :is="renderIcon" />` com uma função que ignora os attrs
        recebidos, então aria-hidden (e qualquer outro attr fora de
        class/style, que o Vue mescla à parte) nunca chega ao span interno.
        Mesma saída visual do ícone, sem depender desse encaminhamento —
        precedente já usado em KanbanConditionPill.vue.
      -->
      <span
        :class="statusRow.icon"
        class="size-3 shrink-0"
        aria-hidden="true"
      />
      <span>{{ $t(statusRow.labelKey) }}</span>
    </div>
  </div>
</template>
